import { ReactNode, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import styles from '@components/DataTableSortHeader.module.css';
import Icon from '@components/text/Icon';
import TableHeader from '@components/typography/TableHeader';
import useKeyboardFocus from '@hooks/useKeyboardFocus';
import { SortDirectionEnum } from '@typing/Generated';
import { IconName } from '@utils/iconUtils';
import { clickCurrentTarget } from '@utils/keyboardUtils';

type Props<SortBy> = {
  columnSortBy?: SortBy;
  extraInfo?: ReactNode;
  hideMobile?: boolean;
  icon?: IconName;
  messageKey?: string;
  messageValues?: Record<string, ReactNode>;
  setSortBy?: (sortBy: SortBy) => void;
  setSortDirection?: (sortDirection: SortDirectionEnum) => void;
  skip?: boolean;
  sortBy?: SortBy;
  sortDirection?: SortDirectionEnum;
  textEnd?: boolean;
};

const DataTableSortHeader = <SortBy extends unknown>({
  columnSortBy,
  extraInfo,
  hideMobile,
  icon,
  messageKey,
  messageValues,
  setSortBy,
  setSortDirection,
  skip = false,
  sortBy,
  sortDirection,
  textEnd = false
}: Props<SortBy>) => {
  const intl = useIntl();
  const keyboardFocus = useKeyboardFocus();

  const handleClick = useCallback(() => {
    if (columnSortBy && sortBy === columnSortBy) {
      if (sortDirection === SortDirectionEnum.ascending) {
        setSortDirection?.(SortDirectionEnum.descending);
      } else {
        setSortDirection?.(SortDirectionEnum.ascending);
      }
    } else if (columnSortBy) {
      setSortBy?.(columnSortBy);
      setSortDirection?.(SortDirectionEnum.ascending);
    }
  }, [columnSortBy, setSortBy, setSortDirection, sortBy, sortDirection]);

  if (skip) {
    return null;
  }

  return (
    <>
      {columnSortBy ? (
        <>
          <th
            className={`${sortBy === columnSortBy ? styles.active : styles.inactive} ${styles.clickableHeading}  ${
              keyboardFocus ? styles.focus : ''
            } ${textEnd ? styles.labelEnd : ''}`}
            onClick={handleClick}
            onKeyUp={clickCurrentTarget}
            tabIndex={0}
          >
            <span className={`${styles.align}  ${hideMobile ? styles.hideMobile : ''}`}>
              {extraInfo}
              {icon ? (
                <>
                  <Icon
                    ariaLabelKey="accessibility.ariaLabels.sortableColumn"
                    className={styles.headingIcon}
                    icon={icon}
                  />
                  {sortBy === columnSortBy ? (
                    <>
                      {sortDirection === SortDirectionEnum.ascending ? (
                        <Icon ariaLabelKey="accessibility.ariaLabels.sortDown" icon="chevronDown" />
                      ) : (
                        <Icon ariaLabelKey="accessibility.ariaLabels.sortUp" icon="chevronUp" />
                      )}
                    </>
                  ) : (
                    <Icon ariaLabelKey="accessibility.ariaLabels.sort" icon="sort" />
                  )}
                </>
              ) : (
                <>
                  {sortBy === columnSortBy ? (
                    <TableHeader
                      icon={sortDirection === SortDirectionEnum.ascending ? 'chevronDown' : 'chevronUp'}
                      text={intl.formatMessage({ id: messageKey }, messageValues)}
                    />
                  ) : (
                    <TableHeader icon="sort" text={intl.formatMessage({ id: messageKey }, messageValues)} />
                  )}
                </>
              )}
            </span>
          </th>
        </>
      ) : (
        <th className={textEnd ? styles.labelEnd : ''}>
          {extraInfo}
          {icon && (
            <Icon ariaLabelKey="accessibility.ariaLabels.sortableColumn" className={styles.headingIcon} icon={icon} />
          )}
          <FormattedMessage id={messageKey} values={messageValues} />
        </th>
      )}
    </>
  );
};

export default DataTableSortHeader;
